import type { ApiSchema } from "../apiSchema";
import { readPreparedWorkflow, readStepPlan, type PreparedWorkflow, type StepPlan } from "../content/stepPlans";
import { jsonBody, requestJson, withQuery } from "../http/httpClient";
import { readArray, readBoolean, readInteger, readNullableString, readObject, readOneOf, readString } from "../http/jsonDecoding";
import { readThreadBindings, readThreadEntry, type AgentRoute, type ApiKeyRoute, type Mention, type ThreadBindings, type ThreadEntry, type ThreadSettings } from "./liveMessages";
import {
  decodeEntriesPage,
  decodeSearchHits,
  decodeThreadOverview,
  decodeThreadSummaries,
  decodeTurnSummary,
  readKeptInputNotes,
  STEP_PROGRESS_STATES,
  type EntriesPage,
  type KeptInputNote,
  type ThreadOverview,
  type ThreadSearchHit,
  type ThreadSummary,
  type TurnKind,
  type TurnSummary,
} from "./threadDecoders";

/**
 * Threads (module C, `docs/components/threads.md`): starting, reading, searching, forking
 * and writing in a Thread. Every write carries the client's idempotency key, so a retry
 * returns the first result (T-1). Routes: `server/src/dmw/api/routes/threads/conversation.py`.
 * Hosting (attach, close, hand-off) is in `threadHostingApi.ts`; live updates come over
 * the browser socket (`features/threads/live/`).
 */

/** At most this many entries per page (`threads.maximum_entries_per_page`). */
export const ENTRIES_PAGE_LIMIT = 200;
/** The most recently active Threads the list shows. */
export const THREAD_LIST_LIMIT = 100;
const SEARCH_LIMIT = 30;

/** A new idempotency key for one write the user asked for. */
export function newClientKey(): string {
  return crypto.randomUUID();
}

export function threadPath(threadId: string, rest = ""): `/${string}` {
  return `/threads/${encodeURIComponent(threadId)}${rest}`;
}

/** The project's Threads, most recently active first. */
export function listThreads(projectId: string, signal?: AbortSignal): Promise<readonly ThreadSummary[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/threads`, { limit: THREAD_LIST_LIMIT }),
    signal,
    decodeResponse: decodeThreadSummaries,
  });
}

/**
 * Of the Workflow items `among`, those the project's Threads were started from most recently,
 * newest start first, each once, up to `limit`: what a Workflow list offers as recently used,
 * among the Workflows it could show there (F123). Writes nothing.
 */
export function listRecentWorkflows(projectId: string, among: readonly string[], limit: number, signal?: AbortSignal): Promise<readonly string[]> {
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(projectId)}/threads/recent-workflows`,
    body: jsonBody({ among, limit }),
    signal,
    decodeResponse: (body) => readArray(readObject(body, "recent Workflows")["workflows"], "recent Workflows", (item) => readString({ item }, "item")),
  });
}

export function readThread(threadId: string, signal?: AbortSignal): Promise<ThreadOverview> {
  return requestJson({ method: "GET", path: threadPath(threadId), signal, decodeResponse: decodeThreadOverview });
}

/** Entries of the Thread's path numbered `after + 1` to `after + limit`. */
export function readEntries(threadId: string, after: number, limit: number, signal?: AbortSignal): Promise<EntriesPage> {
  return requestJson({
    method: "GET",
    path: withQuery(threadPath(threadId, "/entries"), { after, limit }),
    signal,
    decodeResponse: decodeEntriesPage,
  });
}

export function listForks(threadId: string, signal?: AbortSignal): Promise<readonly ThreadSummary[]> {
  return requestJson({ method: "GET", path: threadPath(threadId, "/forks"), signal, decodeResponse: decodeThreadSummaries });
}

/** Entries and titles in every project the user may read. */
export function searchThreads(query: string, signal?: AbortSignal): Promise<readonly ThreadSearchHit[]> {
  return requestJson({
    method: "GET",
    path: withQuery("/search/threads", { query, limit: SEARCH_LIMIT }),
    signal,
    decodeResponse: decodeSearchHits,
  });
}

/**
 * The longest Thread title the server takes, in characters: `title.maxLength` of
 * `StartThreadBody` and `ForkBody` in schema/openapi.json (a test holds them equal).
 */
export const THREAD_TITLE_MAX_LENGTH = 200;

/** A title cut to the server's limit, counting characters as the server does, not UTF-16 units. */
export function fitThreadTitle(title: string): string {
  return Array.from(title).slice(0, THREAD_TITLE_MAX_LENGTH).join("");
}

export interface StartThreadRequest {
  readonly projectId: string;
  readonly title: string;
  /** A Workflow `VersionRef`, or `null` for a free Thread. */
  readonly workflow: string | null;
  readonly bindings: ThreadBindings;
  readonly clientRequestId: string;
  /**
   * Values for the Workflow's inputs, by exposed name or by destination `<step key>.<input>`
   * (ADR-0032 D7, built provisionally): each step takes its own when it runs. Sent only
   * when not empty.
   */
  readonly inputValues?: Readonly<Record<string, unknown>>;
  /** The LLM routes the start chose (F106): the Thread's default and agent steps' own, over the server's default settings. */
  readonly routes?: { readonly route: AgentRoute | ApiKeyRoute; readonly step_routes: Readonly<Record<string, AgentRoute | ApiKeyRoute>> } | null;
}

/** Start a Thread with the server's default settings and the routes the start chose, if any; without them the host picks a route on attach. */
export function startThread(request: StartThreadRequest): Promise<ThreadOverview> {
  const body = {
    title: fitThreadTitle(request.title),
    workflow: request.workflow,
    bindings: { ...request.bindings, selection: request.bindings.selection ?? [], context_outputs: request.bindings.context_outputs ?? [] },
    settings: null,
    client_request_id: request.clientRequestId,
  } satisfies ApiSchema<"StartThreadBody">;
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(request.projectId)}/threads`,
    body: jsonBody({
      ...body,
      bindings: { ...body.bindings, selection: request.bindings.selection?.length ? request.bindings.selection : undefined, context_outputs: request.bindings.context_outputs?.length ? request.bindings.context_outputs : undefined },
      input_values: request.inputValues !== undefined && Object.keys(request.inputValues).length > 0 ? request.inputValues : undefined,
      routes: request.routes ?? undefined,
    }),
    decodeResponse: decodeThreadOverview,
  });
}


export interface StartPreparation {
  readonly projectId: string;
  /** The Workflow `VersionRef` the start runs. */
  readonly workflow: string;
  readonly bindings: ThreadBindings;
  /** Values as the start would give them: by exposed input name, or by destination `<step key>.<input>`. */
  readonly inputValues: Readonly<Record<string, unknown>>;
}

/**
 * What a start on these bindings with these values gives the Workflow and each of its steps,
 * as the server prepares it (ADR-0032 D7, built provisionally): the start form, each step's
 * plan and what the start asks for. Writes nothing.
 */
export function prepareStart(request: StartPreparation, signal?: AbortSignal): Promise<PreparedWorkflow> {
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(request.projectId)}/threads/start-preparation`,
    body: jsonBody({ workflow: request.workflow, bindings: request.bindings, input_values: request.inputValues }),
    signal,
    decodeResponse: (body) => readPreparedWorkflow(readObject(body, "prepared workflow")),
  });
}

/**
 * The Workflow versions a start on these bindings can follow, as module B decides it (F28,
 * F123): started from documents, those that take them. Writes nothing.
 */
export function listStartWorkflows(projectId: string, bindings: ThreadBindings, signal?: AbortSignal): Promise<ReadonlySet<string>> {
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(projectId)}/threads/start-workflows`,
    body: jsonBody({ bindings }),
    signal,
    decodeResponse: (body) => new Set(readArray(readObject(body, "start Workflows")["workflows"], "start Workflows", (item) => readString({ item }, "item"))),
  });
}

/** A run's values for one step's inputs, by name, as its run would send them. */
export interface StepRunDraft {
  readonly stepKey: string;
  readonly values: Readonly<Record<string, unknown>>;
}

/**
 * Each step of the Thread's Workflow with what has run, as module B plans it (ADR-0032 D7),
 * and with a run's draft values for one step when given; empty for a free Thread. Writes nothing.
 */
export function readStepPlans(threadId: string, draft: StepRunDraft | null, signal?: AbortSignal): Promise<readonly ThreadStepPlan[]> {
  return requestJson({
    method: "POST",
    path: threadPath(threadId, "/step-plans"),
    body: jsonBody(draft === null ? {} : { step_key: draft.stepKey, input_values: draft.values }),
    signal,
    decodeResponse: (body) => readArray(body, "step plans", readThreadStepPlan),
  });
}

/** The route an agent step runs on, from the Thread's settings (F106). */
export interface StepRoute {
  /** `null` until the host picks a default and the step has no choice of its own. */
  readonly route: AgentRoute | ApiKeyRoute | null;
  /** The step's own choice, not the Thread's default. */
  readonly own: boolean;
}

/** A step's plan in a Thread: with the route an agent step runs with (`dmwp:runsWith`). */
export interface ThreadStepPlan extends StepPlan {
  readonly runsWith: StepRoute | null;
}

function readThreadStepPlan(value: unknown): ThreadStepPlan {
  const runsWith = readObject(value, "step plan")["runs_with"];
  if (runsWith === undefined || runsWith === null) return { ...readStepPlan(value), runsWith: null };
  const object = readObject(runsWith, "runs_with");
  return { ...readStepPlan(value), runsWith: { route: object["route"] === null ? null : readTurnRoute(object["route"]), own: readBoolean(object, "own") } };
}

function readTurnRoute(value: unknown): AgentRoute | ApiKeyRoute {
  const route = readObject(value, "route");
  if (readString(route, "kind") === "api_key") return { kind: "api_key", route_id: readString(route, "route_id"), model_id: readString(route, "model_id") };
  const effort = route["reasoning_effort"];
  return {
    kind: readOneOf(route, "kind", ["agent"] as const),
    agent_id: readString(route, "agent_id"),
    model_id: readNullableString(route, "model_id"),
    ...(effort === undefined || effort === null ? {} : { reasoning_effort: readString(route, "reasoning_effort") }),
  };
}

/** What a fork asks for; its preview asks for the same (D37). */
export interface ForkChoice {
  readonly atEntry: number;
  /** Other bindings and Workflow version than at the fork point; omitted: the same. */
  readonly onto?: { readonly bindings: ThreadBindings; readonly workflow: string | null };
  /** Another version of the Thread's corpus; the server moves the subject and selection to their documents' versions there (D6). */
  readonly corpusVersion?: string;
}

export interface ForkRequest extends ForkChoice {
  readonly title: string;
  readonly clientRequestId: string;
}

function forkChoiceBody(choice: ForkChoice) {
  return {
    at_entry: choice.atEntry,
    ...(choice.corpusVersion === undefined ? {} : { corpus_version: choice.corpusVersion }),
    ...(choice.onto === undefined ? {} : { bindings: { ...choice.onto.bindings, selection: choice.onto.bindings.selection ?? [], context_outputs: choice.onto.bindings.context_outputs ?? [] }, workflow: choice.onto.workflow }),
  } satisfies ApiSchema<"ForkRequestBody">;
}

export interface ForkPreviewStep {
  readonly stepKey: string;
  /** The step version the fork's Workflow runs under this key, which names it. */
  readonly step: string;
  readonly state: ApiSchema<"StepProgressState">;
  /** It keeps its result from the fork point. */
  readonly kept: boolean;
}

/** What a fork would keep and run, from the fork's own plan (D37). */
export interface ForkPreview {
  readonly atEntry: number;
  /** The fork's Workflow, whose definitions name its steps. */
  readonly workflow: string | null;
  readonly continueWithStep: string | null;
  readonly steps: readonly ForkPreviewStep[];
  readonly notes: readonly KeptInputNote[];
}

export function previewFork(threadId: string, choice: ForkChoice, signal?: AbortSignal): Promise<ForkPreview> {
  return requestJson({
    method: "POST",
    path: threadPath(threadId, "/fork-preview"),
    body: jsonBody(forkChoiceBody(choice)),
    signal,
    decodeResponse: (body) => {
      const preview = readObject(body, "fork preview");
      return {
        atEntry: readInteger(preview, "at_entry"),
        workflow: readNullableString(preview, "workflow"),
        continueWithStep: readNullableString(preview, "continue_with_step"),
        steps: readArray(preview["steps"], "fork preview steps", (item) => {
          const step = readObject(item, "fork preview step");
          return { stepKey: readString(step, "step_key"), step: readString(step, "step"), state: readOneOf(step, "state", STEP_PROGRESS_STATES), kept: readBoolean(step, "kept") };
        }),
        notes: readKeptInputNotes(preview["kept_input_changes"]),
      };
    },
  });
}

/** The Thread as it was at an entry: what a fork there inherits unless it names other versions (T-10, RF-04). */
export interface ForkPoint {
  readonly atEntry: number;
  readonly bindings: ThreadBindings;
  readonly workflow: string | null;
}

export function readForkPoint(threadId: string, atEntry: number, signal?: AbortSignal): Promise<ForkPoint> {
  return requestJson({
    method: "GET",
    path: withQuery(threadPath(threadId, "/fork-point"), { at_entry: atEntry }),
    signal,
    decodeResponse: (body) => {
      const point = readObject(body, "fork point");
      return { atEntry: readInteger(point, "at_entry"), bindings: readThreadBindings(point["bindings"]), workflow: readNullableString(point, "workflow") };
    },
  });
}

/** A new Thread the user hosts, saved until they attach a connector (T-10). */
export function forkThread(threadId: string, request: ForkRequest): Promise<ThreadOverview> {
  const body = {
    ...forkChoiceBody(request),
    title: fitThreadTitle(request.title),
    client_request_id: request.clientRequestId,
  } satisfies ApiSchema<"ForkBody">;
  return requestJson({ method: "POST", path: threadPath(threadId, "/fork"), body: jsonBody(body), decodeResponse: decodeThreadOverview });
}

export interface PostMessageRequest {
  readonly text: string;
  readonly mentions: readonly Mention[];
  readonly clientMessageId: string;
}

export function postMessage(threadId: string, request: PostMessageRequest): Promise<ThreadEntry> {
  const body = {
    text: request.text,
    mentions: [...request.mentions],
    client_message_id: request.clientMessageId,
  } satisfies ApiSchema<"PostMessageBody">;
  return requestJson({ method: "POST", path: threadPath(threadId, "/messages"), body: jsonBody(body), decodeResponse: readThreadEntry });
}

export type AgentSetupKind = ApiSchema<"AgentSetupKind">;

/**
 * Settings as the server takes them. The live types read a defaulted field as optional;
 * the server always sends it, and an older Thread's settings get the defaults.
 */
export function completeSettings(settings: ThreadSettings): ApiSchema<"ThreadSettings"> {
  return {
    ...settings,
    fallback_routes: settings.fallback_routes ?? [],
    agent_setup: settings.agent_setup ?? "extend",
    stop_before_agent_steps: settings.stop_before_agent_steps ?? false,
  };
}

export interface TurnRequest {
  readonly kind: TurnKind;
  /** A step key of the Workflow, for `step` turns. */
  readonly stepKey: string | null;
  readonly clientRequestId: string;
  /** `dmw_only`: "Try with DMW only", without the host's own agent setup (agent-profiles.md AP-6). */
  readonly agentSetup?: AgentSetupKind;
  /**
   * Values of the step's declared inputs changed for this run (ADR-0032 D7, built
   * provisionally, awaiting acceptance): sent as `input_values` only when not empty, so a
   * server without the field takes every other request as before.
   */
  readonly inputValues?: Readonly<Record<string, unknown>>;
  /**
   * A tool version run without a step (W2-6): a `step` turn with no step key, its inputs in
   * `inputValues` and its parameters here.
   */
  readonly tool?: { readonly reference: string; readonly parameters: Readonly<Record<string, unknown>> };
}

/** Run now: a reply, a step by key (also a rerun), or a tool version. */
export function requestTurn(threadId: string, request: TurnRequest): Promise<ThreadEntry> {
  const body = {
    kind: request.kind,
    step_key: request.stepKey,
    client_request_id: request.clientRequestId,
    agent_setup: request.agentSetup ?? "extend",
    ...(request.tool === undefined ? {} : { tool: request.tool.reference, parameters: request.tool.parameters }),
  } satisfies ApiSchema<"RequestTurnBody">;
  const inputValues = request.inputValues ?? {};
  const sent = Object.keys(inputValues).length === 0 ? body : { ...body, input_values: inputValues };
  return requestJson({ method: "POST", path: threadPath(threadId, "/turns"), body: jsonBody(sent), decodeResponse: readThreadEntry });
}

export function cancelTurn(threadId: string, turnId: string): Promise<TurnSummary> {
  return requestJson({
    method: "POST",
    path: threadPath(threadId, `/turns/${encodeURIComponent(turnId)}/cancel`),
    decodeResponse: decodeTurnSummary,
  });
}

export interface HumanInputRequest {
  readonly answersEntry: number;
  readonly comment: string | null;
  readonly choice: string | null;
  /** For `review_output` responses: the confirmed result. */
  readonly result: ApiSchema<"HumanInputBody">["result"];
  /** For `workspace_file` responses: the file's path in the host's workspace (Q-81). */
  readonly workspaceFile?: string | null;
  /** For `keep_memory` responses: the memory the person keeps (ADR-0031 D5). */
  readonly memory?: ApiSchema<"KeepMemoryBody"> | null;
  readonly clientRequestId: string;
}

export function answerHumanStep(threadId: string, request: HumanInputRequest): Promise<ThreadEntry> {
  const body = {
    answers_entry: request.answersEntry,
    comment: request.comment,
    choice: request.choice,
    result: request.result,
    workspace_file: request.workspaceFile ?? null,
    memory: request.memory ?? null,
    client_request_id: request.clientRequestId,
  } satisfies ApiSchema<"HumanInputBody">;
  return requestJson({ method: "POST", path: threadPath(threadId, "/human-inputs"), body: jsonBody(body), decodeResponse: readThreadEntry });
}

export interface TalkRequest {
  /** The waiting review step's request. */
  readonly reviewEntry: number;
  readonly request: string;
  /** The result as the review form holds it, edits included; `null` when unchanged. */
  readonly result: HumanInputRequest["result"];
  readonly clientRequestId: string;
}

/** Talk with the agent of the waiting review, in its own session (F105). */
export function talkWithAgent(threadId: string, talk: TalkRequest): Promise<ThreadEntry> {
  const body = {
    review_entry: talk.reviewEntry,
    request: talk.request,
    result: talk.result,
    client_request_id: talk.clientRequestId,
  } satisfies ApiSchema<"TalkBody">;
  return requestJson({ method: "POST", path: threadPath(threadId, "/talks"), body: jsonBody(body), decodeResponse: readThreadEntry });
}

/** Replace settings, conditional on the last entry the host saw (host only). */
/** Rename the Thread, conditional on the last entry seen (host only, F68). */
export function renameThread(threadId: string, expectedLastEntryNumber: number, title: string): Promise<ThreadEntry> {
  const body = { expected_last_entry_number: expectedLastEntryNumber, title } satisfies ApiSchema<"RenameBody">;
  return requestJson({ method: "POST", path: threadPath(threadId, "/title"), body: jsonBody(body), decodeResponse: readThreadEntry });
}

export function changeSettings(
  threadId: string,
  expectedLastEntryNumber: number,
  settings: ThreadSettings,
  bindings: ThreadBindings,
): Promise<ThreadEntry> {
  const body = {
    expected_last_entry_number: expectedLastEntryNumber,
    settings: completeSettings(settings),
    bindings: { ...bindings, selection: bindings.selection ?? [], context_outputs: bindings.context_outputs ?? [] },
  } satisfies ApiSchema<"ChangeSettingsBody">;
  return requestJson({ method: "PATCH", path: threadPath(threadId, "/settings"), body: jsonBody(body), decodeResponse: readThreadEntry });
}

export type WorkflowActionEvent = ApiSchema<"WorkflowActionBody">["event"];

/** Skip a step, or leave or resume the Workflow (host only, FR-WF-4); another Workflow starts by `switchWorkflow` (F126). */
export function workflowAction(
  threadId: string,
  event: Exclude<WorkflowActionEvent, "started">,
  stepKey: string | null,
  expectedLastEntryNumber: number,
  /** With `run_to`: the values typed into the current step, which its run takes (PC-3). */
  inputValues?: Readonly<Record<string, unknown>>,
): Promise<ThreadEntry> {
  const body = {
    event,
    step_key: stepKey,
    workflow: null,
    expected_last_entry_number: expectedLastEntryNumber,
  } satisfies ApiSchema<"WorkflowActionBody">;
  const sent = inputValues === undefined || Object.keys(inputValues).length === 0 ? body : { ...body, input_values: inputValues };
  return requestJson({ method: "POST", path: threadPath(threadId, "/workflow-actions"), body: jsonBody(sent), decodeResponse: readThreadEntry });
}
